import {ActivePaymentPlan} from "../../../declarations";
import {PaymentPlanFrequency, PlanRating} from "../../../enums";
import {PartnerBase, PaymentDetails} from "../index";

export type ContributionLinkResponse = {
  url: string;
};

type PaymentPlanOfferData = {
  segment_name: string;
  option_name: string;
  account_total?: number;
  one_time_discount: number;
  max_plan_discount: number;
  max_plan_length_sif: number;
  max_plan_length_pif: number;
  fees_added_to_first_installment?: number;
  config_source_id?: string;
};

export type HardshipPaymentPlanOffer = {
  number_of_installments: number;
  installment_size: number;
};

export type PaymentPlanOffer = {
  account_id: string;
  installment_frequency: PaymentPlanFrequency;
  number_of_installments: number;
  installment_size: number;
  discount_fraction: number;
  total_amount: number;
  amount_before_discount_cents: number;
  is_recommended: false;
  abstract_payment_plan_name: string;
  start_date: string;
  end_date: string;
  resolution_expert_only: boolean;
  waived_fees?: boolean;
  waived_interest?: boolean;
  is_interim_offer?: boolean;
  hardship_payment_plan?: HardshipPaymentPlanOffer;
  data?: PaymentPlanOfferData;
};

export type CancellationReason =
  | "payment_date_doesnt_work"
  | "payment_amount_too_high"
  | "need_payment_break"
  | "other";

export type CancelledBy = "system" | "agent" | "user";

export type RevokedData = {
  reason: CancellationReason;
  extra?: string;
  cancelled_by: CancelledBy;
};

export type FpPaymentPlanOffer = PaymentPlanOffer & {
  fp_account_id: string;
  is_fp_partial_offer?: boolean;
};

export type BundlePaymentPlanOffers = {
  weekly: PaymentPlanOffer[];
  bi_weekly: PaymentPlanOffer[];
  monthly: PaymentPlanOffer[];
  one_time: PaymentPlanOffer[];
  first_and_fifteenth: PaymentPlanOffer[];
};

export type PaymentPlanOffers = {
  [key: string]: (PaymentPlanOffer | FpPaymentPlanOffer)[] | BundlePaymentPlanOffers | null;
  plans: PaymentPlanOffer[];
  pre_co_partial: PaymentPlanOffer[];
  bi_weekly: PaymentPlanOffer[];
  monthly: PaymentPlanOffer[];
  recommended: PaymentPlanOffer[];
  weekly: PaymentPlanOffer[];
  one_time: PaymentPlanOffer[];
  first_and_fifteenth: PaymentPlanOffer[];
  hardship: PaymentPlanOffer[];
  fp_partial: FpPaymentPlanOffer[];
  bundle: BundlePaymentPlanOffers | null;
};

export type PlanType = "one-time" | "installments";

export interface ICustomizedInstallment {
  startDate: string;
  amountToPay: number;
  dueDate?: string;
}

export type InstallmentStatus = "unpaid" | "paid" | "error" | "late" | "pending" | "credit" | "partial" | "rescheduled";

export interface BundleAttributions {
  account_id: number;
  account_public_id: string;
  amount_cents: number;
}

export type UserDashboardInstallment = {
  amount_cents: number;
  due_date: Date;
  installment_status: InstallmentStatus;
  failed_payment_reason?: string;
  last_payment_attempt: Date;
  payment_id: string;
  id: string;
  credit_entity: boolean;
  bundle_attributions?: BundleAttributions[];
};

export type PaymentPlanBuilderFrequency = "weekly" | "bi_weekly" | "monthly" | "first_and_fifteenth";

export enum MatrixPlanType {
  MATRIX_NORMAL = "MATRIX_NORMAL",
  MATRIX_BUNDLE = "MATRIX_BUNDLE",
  MATRIX_DOWN_PAYMENT = "MATRIX_DOWN_PAYMENT",
  MATRIX_DOWN_PAYMENT_BUNDLE = "MATRIX_DOWN_PAYMENT_BUNDLE",
  MATRIX_HARDSHIP = "MATRIX_HARDSHIP",
  MATRIX_HARDSHIP_BUNDLE = "MATRIX_HARDSHIP_BUNDLE",
}

export type SingleInstallmentPlanParts = {
  installment_amount: string;
  start_date: string;
};

export type PaymentPlanParts = SingleInstallmentPlanParts & {
  num_of_payments: string;
  frequency: string;
  end_date: string;
};

export type PaymentPlanTerms = {
  account_ids: string[];
  last_4_digits: string;
  payment_method: string;
  payment_plan_parts: PaymentPlanParts[];
  single_installment_plan_parts: SingleInstallmentPlanParts[];
  remainder?: string;
  type?: string;
  total_amount: string;
};

export type PaymentPlanTermsForLetter = {
  payment_plan_parts: PaymentPlanParts[];
  single_installment_plan_parts: SingleInstallmentPlanParts[];
  remainder?: string;
  total_amount: string;
};

export type UserPlanTerms = {
  userId: string;
  paymentPlanTerms: PaymentPlanTerms | PaymentPlanTermsForLetter;
};

export type PaymentPlanOfferResponse = {
  success: boolean;
  offer?: PaymentPlanOffer;
  partner_details: PartnerBase;
};

export type PaymentPlanOfferBase = {
  installment_frequency: PaymentPlanFrequency;
  number_of_installments: number;
  installment_size?: number;
  discount_fraction: number;
  total_amount: number;
  amount_before_discount_cents: number;
  abstract_payment_plan_name: string;
  start_date: string;
  end_date: string;
};

export type PaymentPlanLinkRequest = {
  start_date: string;
  abstract_payment_plan_name: string;
};

export type MatrixFirstPlanDetails = {
  start_date: string;
  number_of_installments: number;
  installment_size: number;
};

export type PaymentPlanMatrixLinkRequest = {
  first_plan_details?: MatrixFirstPlanDetails;
  start_date: string;
  abstract_payment_plan_name: string;
  plan_type: MatrixPlanType;
};

export type MatrixPlanOfferResponse = {
  success: boolean;
  offer?: IPlanMatrix;
  partner_details: PartnerBase;
};

export type PaymentPlanExternal = {
  activePaymentPlan?: ActivePaymentPlan;
  isOnPlan: boolean;
};

export type NonLinearPaymentPlan = {
  installments: {
    due_date: string;
    amount_cents: number;
  }[];
  payment_plans: {
    start_date: string;
    end_date: string;
    installment_frequency: PaymentPlanFrequency;
    number_of_installments: number;
    installment_size: number;
  }[];
  payment_details?: PaymentDetails;
  remainder?: number;
  is_hardship_plan?: boolean;
  is_bundle_plan?: boolean;
};

export type GuestPaymentDetailsResponse = {
  first_name: string;
  balance_amount_cents: number;
};

export type PaymentPlanOfferDatesResponse = {
  start_date: string;
  end_date: string;
};

export type FirstPlan = {
  number_of_installments: number;
  installment_size: number;
  start_date?: string;
};

export interface IPlanMatrix {
  total_amount: number;
  start_date: string;
  installment_size: number;
  discount_fraction: number;
  number_of_installments: number;
  abstract_payment_plan_name: string;
  installment_frequency: PaymentPlanBuilderFrequency;
  first_plan_details: FirstPlan;
  color: PlanRating;
  restricted: boolean;
  plan_type: MatrixPlanType;
  remainder: number;
}

export interface PaymentPlanTabMatrix {
  weekly: (IPlanMatrix | null)[][];
  bi_weekly: (IPlanMatrix | null)[][];
  first_and_fifteenth: (IPlanMatrix | null)[][];
  monthly: (IPlanMatrix | null)[][];
}

export interface ICustomizedPaymentPlanDetails {
  planStartDate: string;
  totalAmount: number;
  customizedPaymentPlan: NonLinearPaymentPlan;
  remainder?: number;
  isHardship?: boolean | string;
  isBundle?: boolean;
}
